import { Progress } from "api/types";
import { IcbtFeedbackStateCell } from "components/patients/patientCard/icbt/tablecells/IcbtFeedbackStateCell";
import { PatientNotesCellText } from "components/patients/patientCard/icbt/tablecells/PatientNotesCellText";
import { styled } from "styled-components";
import { ProgramsAndTreatments } from "../../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { IcbtCommunicationWithPatientData } from "../hooks/useCategorizedTodoCommunication";
import { FeedbackDateText } from "./FeedbackDateText";
import { PatientName, PatientNameProps } from "./PatientName";
import { ProgramItem } from "./ProgramItem";
import { type QuestionnaireCounts } from "hooks/icbt/useGetQuestionnaireCountsPerProgramQuery";
import { UserAnswer } from "core/types/index";

export type UserAnswersByRequestIdByUserId = Record<
  string,
  Record<string, UserAnswer[]>
>;
const ItemContainer = styled.li`
  all: unset;
  padding: 10px;
  border-bottom: 1px solid #e0e0e0;
  & > * {
    flex: 1;
  }
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.COLORS.RIPPLE};
  }
`;
const Clickable = styled.button`
  all: unset;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  & > * {
    flex: 1 1 20%;
  }
`;

type FeedbackItemPatientData = PatientNameProps & {
  userId: number;
};

export interface HandlerProps {
  icbtCommunication: IcbtCommunicationWithPatientData<FeedbackItemPatientData>;
  program?: ProgramsAndTreatments["programs"][0];
  treatment?: ProgramsAndTreatments["treatments"][0];
}

interface Props {
  displayDropdownUpwards?: boolean;
  item: IcbtCommunicationWithPatientData<FeedbackItemPatientData>;
  progress?: Progress;
  programsAndTreatments?: ProgramsAndTreatments;
  questionnaireCounts: QuestionnaireCounts;
  userAnswersByRequestIdByUserId: UserAnswersByRequestIdByUserId;
  done?: boolean;
  onClick: (item: HandlerProps) => void;
}

export const FeedbackItem = ({
  displayDropdownUpwards,
  item: icbtCommunication,
  progress,
  programsAndTreatments,
  onClick,
  questionnaireCounts,
  userAnswersByRequestIdByUserId,
  done = false,
}: Props) => {
  const program = programsAndTreatments?.programs[icbtCommunication.program_id];
  const treatment =
    programsAndTreatments?.treatments[icbtCommunication.treatment_id];

  const icbtCommunicationProps = icbtCommunication
    ? {
        feedback_date: icbtCommunication?.feedback_date,
        feedback_date_ignored_at: icbtCommunication?.feedback_date_ignored_at,
        request_id: icbtCommunication?.request_id,
      }
    : undefined;

  const totalQuestionnairesInProgram =
    questionnaireCounts[program?.id ?? ""] ?? 0;

  const questionnairesInProgramAnswered =
    userAnswersByRequestIdByUserId[icbtCommunication.patient_user_id]?.[
      icbtCommunication.request_id
    ]?.length ?? 0;

  return (
    <ItemContainer>
      <Clickable
        onClick={(e) => {
          e.preventDefault();
          onClick({ icbtCommunication, program, treatment });
        }}
      >
        <FeedbackDateText
          feedbackDate={icbtCommunication.feedback_date}
          hasFeedback={icbtCommunication.feedbacks.length > 0}
          done={done}
        />
        <PatientName patient={icbtCommunication.patient} />
        <ProgramItem
          program={program}
          treatment={treatment}
          progress={progress}
        />
        <PatientNotesCellText
          questionnairesInProgramAnswered={questionnairesInProgramAnswered}
          totalQuestionnairesInProgram={totalQuestionnairesInProgram}
          patientNoteCount={icbtCommunication.responses.length}
        />
        <IcbtFeedbackStateCell
          displayDropdownUpwards={displayDropdownUpwards}
          feedbacks={icbtCommunication.feedbacks}
          communication={icbtCommunicationProps}
        />
      </Clickable>
    </ItemContainer>
  );
};
